import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import Baseball from '@/components/Baseball';
import RadioTower from '@/components/RadioTower';
import { playClick, playSuccess } from '@/utils/sounds';
import { 
  RetroBackground, 
  RetroCard, 
  RetroLogo, 
  RetroButton, 
  RetroInputField,
  RetroText,
  PALETTE,
  DESIGN_TOKENS
} from '@/components/RetroUI';

export default function RegisterPage() {
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    playClick();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    // Check password complexity
    if (!/[A-Z]/.test(password)) {
      toast.error('Password must contain at least one uppercase letter');
      return;
    }
    if (!/[a-z]/.test(password)) {
      toast.error('Password must contain at least one lowercase letter');
      return;
    }
    if (!/\d/.test(password)) {
      toast.error('Password must contain at least one number');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(email, password, username);
      playSuccess();
      toast.success('Account created! Let\'s create your player!');
      navigate('/avatar-setup');
    } catch (error) {
      const message = error.response?.data?.detail || 'Registration failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <RetroBackground variant="cream" className="flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo with Radio Tower and Baseball */}
        <div className="text-center mb-6">
          <div className="flex justify-center items-center gap-4 mb-4">
            <RadioTower size={60} animated={true} />
            <Baseball size={70} />
          </div>
          
          <RetroLogo size="md" />
        </div>

        {/* Register Form Card */}
        <RetroCard title="CREATE ACCOUNT">
          <form onSubmit={handleSubmit} className="space-y-4">
            <RetroInputField
              label="Username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="YourNickname"
              required
              data-testid="register-username-input"
            />
            
            <RetroInputField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="player@example.com"
              required
              data-testid="register-email-input"
            />
            
            <div>
              <RetroInputField
                label="Password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                data-testid="register-password-input"
              />
              <p style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontSize: DESIGN_TOKENS.fontSizes.uiXs,
                color: PALETTE.dirt,
                marginTop: '4px',
              }}>
                Min 8 chars with uppercase, lowercase & number
              </p>
            </div>
            
            <RetroInputField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="••••••••"
              required
              data-testid="register-confirm-password-input"
            />
            
            <RetroButton
              type="submit"
              variant="primary"
              size="md"
              fullWidth
              disabled={loading}
              className="mt-4"
              data-testid="register-submit-btn"
            >
              {loading ? 'CREATING...' : 'JOIN THE LEAGUE'}
            </RetroButton>
          </form>
          
          <div className="mt-6 text-center">
            <RetroText size="sm" color="ink">
              Already a player?{' '}
            </RetroText>
            <Link 
              to="/login" 
              className="hover:underline"
              style={{
                fontFamily: DESIGN_TOKENS.fonts.ui,
                fontWeight: 600,
                fontSize: DESIGN_TOKENS.fontSizes.uiSm,
                color: PALETTE.grass,
              }}
              data-testid="login-link"
            >
              Sign In
            </Link>
          </div>
        </RetroCard>
      </div>
    </RetroBackground>
  );
}
